import { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Lobby from './pages/Lobby/Lobby';
import JoinGame from './pages/JoinGame/JoinGame';
import AiSetup from './pages/AiSetup/AiSetup';
import CountdownPage from './pages/CountdownPage/CountdownPage';
import QuestionScreen from './pages/QuestionScreen/QuestionScreen';
import GameArena from './pages/GameArena/GameArena';
import ScoreScreen from './pages/ScoreScreen/ScoreScreen';
import BonusItems from './pages/BonusItems/BonusItems';
import InfoPage from './pages/InfoPage/InfoPage';
import TeacherDashboard from './pages/TeacherDashboard/TeacherDashboard';
import TeacherGameView from './pages/TeacherDashboard/TeacherGameView';
import TeacherAuth from './pages/TeacherAuth/TeacherAuth';
import Settings from './pages/Settings/Settings';
import StudyPostGameSurvey from './pages/StudyPostGameSurvey/StudyPostGameSurvey';
import ProtectedRoute from './components/ProtectedRoute';
import { StudentGameSessionMonitor } from './components/StudentGameSessionMonitor';
import FloatingSettings from './components/FloatingSettings';
import { NavigationListener } from './components/NavigationListener';
import { useGameStore } from './stores/gameStore';
import { useGameSessionStore } from './stores/gameSessionStore';
import {
  clearStoredSessionData,
  shouldResetFreshStudentEntry,
} from './utils/sessionRecovery';
import { useSafeNavigate } from './hooks/useSafeNavigate';

// Component to handle global settings logic
function GlobalSettings() {
  const location = useLocation();

  // Determine if user is on teacher routes
  const isTeacher = location.pathname.startsWith('/teacher');

  // Extract session and player info from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const sessionId = urlParams.get('sessionId');
  const playerId = urlParams.get('playerId');

  // Extract sessionId from teacher game routes (e.g., /teacher/game/abc123)
  const teacherSessionMatch = location.pathname.match(
    /^\/teacher\/game\/(.+)$/
  );
  const teacherSessionId = teacherSessionMatch ? teacherSessionMatch[1] : null;

  // Use teacher session ID if available, otherwise use URL parameter
  const finalSessionId = teacherSessionId || sessionId;

  // Don't show settings on the settings page itself to avoid confusion
  const shouldShowSettings = location.pathname !== '/settings';

  if (!shouldShowSettings) return null;

  return (
    <FloatingSettings
      isTeacher={isTeacher}
      sessionId={finalSessionId || undefined}
      playerId={playerId || undefined}
    />
  );
}

function FreshStudentSessionGuard() {
  const location = useLocation();
  const navigate = useSafeNavigate();
  const resetGame = useGameStore((state) => state.resetGame);
  const resetSessionStore = useGameSessionStore((state) => state.reset);
  const initialEntryRef = useRef({
    pathname: location.pathname,
    search: location.search,
  });

  useEffect(() => {
    const { pathname, search } = initialEntryRef.current;
    const urlParams = new URLSearchParams(search);
    const shouldReset = shouldResetFreshStudentEntry(pathname, {
      spectator: urlParams.get('spectator') === 'true',
    });

    if (!shouldReset) {
      return;
    }

    clearStoredSessionData();
    resetSessionStore();
    resetGame();
    navigate('/', { replace: true });
  }, [navigate, resetGame, resetSessionStore]);

  return null;
}

function App() {
  return (
    <div className="app">
      <FreshStudentSessionGuard />
      <NavigationListener />
      <StudentGameSessionMonitor>
        <Routes>
          <Route path="/" element={<Lobby />} />
          <Route path="/home" element={<Lobby />} />
          <Route path="/lobby" element={<Lobby />} />
          <Route path="/about" element={<InfoPage sectionId="about" />} />
          <Route path="/pricing" element={<InfoPage sectionId="pricing" />} />
          <Route
            path="/data-privacy"
            element={<InfoPage sectionId="privacy" />}
          />
          <Route path="/feedback" element={<InfoPage sectionId="feedback" />} />
          <Route path="/join-game/:gameCode" element={<JoinGame />} />
          <Route path="/ai-setup" element={<AiSetup />} />
          <Route path="/countdown" element={<CountdownPage />} />
          <Route path="/question" element={<QuestionScreen />} />
          <Route path="/game" element={<GameArena />} />
          <Route path="/scores" element={<ScoreScreen />} />
          <Route path="/bonus" element={<BonusItems />} />
          <Route path="/final-scores" element={<ScoreScreen />} />
          <Route path="/study/post-game" element={<StudyPostGameSurvey />} />
          <Route
            path="/teacher"
            element={
              <ProtectedRoute>
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/game/:sessionId"
            element={
              <ProtectedRoute>
                <TeacherGameView />
              </ProtectedRoute>
            }
          />
          <Route path="/teacher-auth" element={<TeacherAuth />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>

        {/* Global Settings Button */}
        <GlobalSettings />
      </StudentGameSessionMonitor>
    </div>
  );
}

export default App;
